import type {
  MentorProfileChangeRequest,
  MentorProfileDraft,
} from "@/lib/api/mentor-profile";
import { extractSpotifyTrackId } from "@/lib/mentor-profile/constants";
import type { WizardBuildStep } from "@/lib/mentor-profile/wizard-steps";
import { PostHogEvents } from "@/lib/posthog-events";

export function mentorProfileReviewRound(
  changeRequests: MentorProfileChangeRequest[] | null | undefined,
) {
  const rounds = new Set((changeRequests ?? []).map((c) => c.created_at));
  return rounds.size + 1;
}

export function spotifyLinkType(url: string | undefined) {
  if (!url) return "unknown";
  if (extractSpotifyTrackId(url)) return "track";
  if (url.includes("/playlist/")) return "playlist";
  if (url.includes("/artist/")) return "artist";
  if (url.includes("/album/")) return "album";
  return "other";
}

export type AudioIntroMethod = "recorded" | "uploaded";

let lastAudioIntroMethod: AudioIntroMethod | undefined;

export function setLastAudioIntroMethod(method: AudioIntroMethod) {
  lastAudioIntroMethod = method;
}

export function consumeLastAudioIntroMethod(): AudioIntroMethod | undefined {
  const method = lastAudioIntroMethod;
  lastAudioIntroMethod = undefined;
  return method;
}

/** Map a build-step Continue to the PRD screen-completed event + props. */
export function buildStepContinueEvent(
  step: WizardBuildStep,
  draft: MentorProfileDraft,
  mentorId: string,
): {
  event: (typeof PostHogEvents)[keyof typeof PostHogEvents];
  props: Record<string, unknown>;
} | null {
  const base = { mentor_id: mentorId };
  switch (step) {
    case "profile_photo":
      return {
        event: PostHogEvents.MENTOR_PROFILE_PHOTO_CONFIRMED,
        props: base,
      };
    case "display_name": {
      const display = draft.display_name?.trim() ?? "";
      const legal = [draft.first_name, draft.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
      return {
        event: PostHogEvents.MENTOR_PROFILE_DISPLAY_NAME_COMPLETED,
        props: {
          ...base,
          used_custom_name: display.length > 0 && display !== legal,
        },
      };
    }
    case "languages":
      return null;
    case "bio":
      return {
        event: PostHogEvents.MENTOR_PROFILE_BIO_COMPLETED,
        props: {
          ...base,
          character_count: draft.bio?.trim().length ?? 0,
        },
      };
    case "spotify":
      return {
        event: PostHogEvents.MENTOR_PROFILE_SPOTIFY_LINK_ADDED,
        props: {
          ...base,
          link_type: spotifyLinkType(draft.favorite_music),
        },
      };
    case "audio":
      return {
        event: PostHogEvents.MENTOR_PROFILE_AUDIO_INTRO_CONFIRMED,
        props: {
          ...base,
          method: consumeLastAudioIntroMethod() ?? "unknown",
        },
      };
    case "vibe":
      return {
        event: PostHogEvents.MENTOR_PROFILE_VIBE_PHOTOS_CONFIRMED,
        props: {
          ...base,
          photos_submitted_count: draft.vibe_images?.length ?? 0,
        },
      };
    case "interests":
      return {
        event: PostHogEvents.MENTOR_PROFILE_INTEREST_PHOTOS_CONFIRMED,
        props: {
          ...base,
          interests: draft.interests ?? [],
          photos_submitted_count: draft.interest_images?.length ?? 0,
        },
      };
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}
